import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ItemBuilder from './ItemBuilder';
import GiftShop from './GiftShop';

// The url key for the open product, mirroring GiftShop's `category`.
const PRODUCT_PARAM = 'product';
import Button from '../../../Button/v2';
import { useEmailInterceptor } from '../../../EmailInterceptor/EmailInterceptor';
import { usePrevious } from '../../../PackageBuilders/MultiPose/hooks';
import giftAddonVisible from './giftAddonVisible';
import gradAddonEligible from '../../../../utils/gradAddonEligibility';
import ItemSubtitle, { isAnnouncement } from './ItemSubtitle';

// Re-exported from their own file now: GiftShop and ItemBuilder both draw the
// price line, and ItemAdder imports GiftShop, so defining it here made the two
// modules import each other. Kept as named exports because existing callers and
// tests reach for them at this path.
export { ItemSubtitle, isAnnouncement };

export default function ItemAdder({
  mutation, addons, backgrounds, options, poses = [], siblingImages = [], onSelectBackground, offer = null, qualifiesForFreeAnnouncements = false, hasFreeMagnets = false, pricelistSeason = null, orderPackage = null, inOrderFlow = false, orderType = null, addedAddons = new Map(), onOpenAdded = () => {}, onProductScreenChange = () => {},
}) {
  const [addonSheetsPerType, setAddonSheetsPerType] = useState({});
  const [pose, setPose] = useState(null);
  const [background, setBackground] = useState(null);
  const { intercept } = useEmailInterceptor();


  // Filter addons before rendering. Senior-ness decided ONCE (pricelist season
  // / any senior pose — see gradAddonEligible for why NOT poses[2], whose
  // index lottery hid announcements).
  const seniorCustomer = gradAddonEligible({ pricelistSeason, poses });

  const filteredAddons = addons.filter((addon) => giftAddonVisible(addon, { seniorCustomer, hasFreeMagnets }));

  // ⚠️ WHICH PRODUCT IS OPEN LIVES IN THE URL, not in component state, so the
  // browser's Back button works. It did not: opening a product changed nothing
  // about the url and added no history entry, so Back left the storefront
  // altogether and dropped the customer out of the add-ons step -- measured,
  // `history.length` stayed at 2 and Back landed on the page before the shelf.
  //
  // Derived rather than mirrored, exactly as GiftShop does with `category`:
  // the browser changes the url, this recomputes, and there is no second copy
  // to fall out of step. It also makes a product screen linkable.
  //
  // A `product` naming something this customer cannot see -- a shared link, an
  // old bookmark, or an add-on `giftAddonVisible` has since dropped -- resolves
  // to the shelf rather than to an empty product screen.
  const history = useHistory();
  const location = useLocation();
  const productParam = new URLSearchParams(location.search).get(PRODUCT_PARAM) || '';
  const selectedAddonType = filteredAddons.some((addon) => addon.cssName === productParam)
    ? productParam
    : '';

  // Push, not replace: opening a product is a place the customer can come Back
  // from. Every other param is carried across -- sch, sid, access_code, pid and
  // optional all have to survive, and dropping one would reset the session.
  const setSelectedAddonType = useCallback((next) => {
    const params = new URLSearchParams(location.search);

    if (next) params.set(PRODUCT_PARAM, next);
    else params.delete(PRODUCT_PARAM);

    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location.pathname, location.search]);

  // Below the derivation above, which it reads. Unconditional either way, so
  // the hook order is the same on every render.
  const previousAddonType = usePrevious(selectedAddonType);

  // The page-level CheckoutBar has to know a product screen is up, because both
  // it and StickyBuyBar are `fixed bottom-0` and they overlap on a phone.
  // ItemAdder is the only thing that knows which of the two screens is showing.
  useEffect(() => {
    onProductScreenChange(selectedAddonType !== '');
  }, [selectedAddonType]);

  const handleConfirmation = (addonSheets) => intercept(() => {
    setAddonSheetsPerType((prev) => ({ ...prev, [selectedAddonType]: addonSheets }));
    // setSelectedAddonType('');
    mutation.mutate({ addonSheets });
  });

  useEffect(() => {
    if (selectedAddonType === previousAddonType) return;

    setPose(null);
    setBackground(null);
  }, [selectedAddonType]);

  // The poses a customer may see on this product, and the first of them as the
  // opening preview.
  //
  // ⚠️ THIS USED TO RE-RANDOMISE EVERY 2 SECONDS. A setInterval swapped the pose
  // or the background of the product screen's preview on a timer, so the image a
  // customer was looking at changed while they read the description and decided
  // on a quantity — and which pose they ended up buying was whichever the timer
  // had landed on when they pressed Continue. The product screen now shows the
  // poses as thumbnails and lets them choose, which is both calmer and the thing
  // they were trying to do.
  const selectablePoses = selectedAddonType === 'sibling'
    ? siblingImages
    : (poses.length > 0 ? poses : siblingImages);

  useEffect(() => {
    if (selectedAddonType === '') return;

    setPose(selectablePoses[0]);
    setBackground(backgrounds[0]);
  }, [selectedAddonType]);

  if (selectedAddonType !== '') {
    if (!pose) return null;
    if (!background) return null;

    const isSiblingPose = pose.id === siblingImages[0]?.id;

    const builder = (
      <ItemBuilder
        isLoading={mutation.isLoading}
        isSiblingPose={isSiblingPose}
        selectedAddonType={selectedAddonType}
        // filteredAddons, NOT the raw list: senior/elementary variants share a
        // css_name, so filtering the raw prop here would show both audiences'
        // rows (at both prices) on the detail screen.
        addons={filteredAddons.filter((addon) => addon.cssName === selectedAddonType)}
        // The whole shelf, for the "You might also like" row on that screen.
        visibleAddons={filteredAddons}
        onSelectProduct={setSelectedAddonType}
        initialItems={addonSheetsPerType[selectedAddonType] || []}
        background={background}
        backgrounds={backgrounds}
        orderPackage={orderPackage}
        pose={pose}
        poses={selectablePoses}
        onSelectPose={setPose}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
        onConfirm={handleConfirmation}
        onCancelProps={{ onClick: () => setSelectedAddonType(''), text: 'Back' }}
      />
    );
    /* The "Continue to Cart" bar used to sit here. Its replacement is
       CheckoutBar, which HolidayBuilder renders at page level -- fixed to the
       viewport, so it does not need to be inside whichever view is showing.
       `showSkipToCart` / `onSkipToCart` went with it rather than being carried
       as dead parameters. */
    return builder;
  }

  /* useEffect(() => {
    addons.forEach(addon => {
      console.log(addon);
      console.log('Addon imageUrl:', addon._attributes.cssName);
    });
  }, [addons]);

  useEffect(() => {
    poses.forEach(pose => {
      console.log(pose);
      console.log('Pose imageUrl:', pose._attributes.url);
    });
  }, [poses]); */

  const handleSelectBackground = () => intercept(() => {
    onSelectBackground(Object.values(addonSheetsPerType).flat());
  });

  // filteredAddons is THE addon filter — this render previously carried its own
  // inline duplicate (gated on poses[0]) that had drifted from it, so fixes to
  // one copy silently didn't reach the page. One implementation only.
  //
  // GiftShop owns the storefront (category sidebar, search, sort, view); this
  // component's remaining job is which products a customer may see and which one
  // they picked.
  return (
    <>
      <GiftShop
        addons={filteredAddons}
        addedAddons={addedAddons}
        inOrderFlow={inOrderFlow}
        onOpenAdded={onOpenAdded}
        orderType={orderType}
        onSelect={setSelectedAddonType}
        poses={poses}
        backgrounds={backgrounds}
        orderPackage={orderPackage}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
      />

      {false && (
        <div>
          <Button onClick={handleSelectBackground}>Select Backgrounds</Button>
        </div>
      )}

    </>
  );
}
